import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';
import { getLeaveGroups } from '../../../admin/Services/MasterdataService';
import { getEligibleReportingManagers } from '../../../admin/Services/EmployeeMasterCsvService';

const DRAWER_WIDTH = 380;

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start',
}));

const EMPTY_FILTERS = {
  status: 'ALL',
  leaveGroup: 'ALL',
  reportingManagerId: 'ALL',
  joiningDateAfter: '',
  joiningDateBefore: '',
  careerStartDateAfter: '',
  careerStartDateBefore: '',
  internshipEndDateAfter: '',
  internshipEndDateBefore: '',
};

const DATE_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/;

const maskDdMmYyyy = (raw) => {
  const digits = String(raw || '')
    .replace(/\D/g, '')
    .slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const parseDdMmYyyy = (value) => {
  if (!value || !String(value).trim()) return null;
  const match = String(value).trim().match(DATE_REGEX);
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

const formatDdMmYyyy = (date) => {
  if (!date) return '';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const getDateFieldError = (value) => {
  if (!value || !String(value).trim()) return '';
  const trimmed = String(value).trim();
  if (trimmed.length < 10) return '';
  if (!parseDdMmYyyy(trimmed)) return 'Use dd/mm/yyyy';
  return '';
};

const isDateValueBlocking = (value) => {
  if (!value || !String(value).trim()) return false;
  const trimmed = String(value).trim();
  if (trimmed.length < 10) return true;
  return !parseDdMmYyyy(trimmed);
};

const constrainAgainstPeer = (value, peerValue, side) => {
  const parsed = parseDdMmYyyy(value);
  if (!parsed) return value;
  const peer = parseDdMmYyyy(peerValue);
  if (!peer) return value;
  if (side === 'after' && parsed > peer) return formatDdMmYyyy(peer);
  if (side === 'before' && parsed < peer) return formatDdMmYyyy(peer);
  return value;
};

const getPairError = (afterValue, beforeValue) => {
  const afterErr = getDateFieldError(afterValue);
  const beforeErr = getDateFieldError(beforeValue);
  if (afterErr || beforeErr) return afterErr || beforeErr;
  const after = parseDdMmYyyy(afterValue);
  const before = parseDdMmYyyy(beforeValue);
  if (after && before && after > before) {
    return 'After date cannot be later than before date';
  }
  return '';
};

const isPairBlocking = (afterValue, beforeValue) =>
  isDateValueBlocking(afterValue) ||
  isDateValueBlocking(beforeValue) ||
  Boolean(getPairError(afterValue, beforeValue));

/**
 * Fully local date pair — only this pair re-renders while typing.
 */
const DateBoundFields = memo(function DateBoundFields({
  label,
  afterKey,
  beforeKey,
  resetVersion,
  datesRef,
  onPairBlockingChange,
}) {
  const [afterValue, setAfterValue] = useState('');
  const [beforeValue, setBeforeValue] = useState('');

  useEffect(() => {
    setAfterValue('');
    setBeforeValue('');
    datesRef.current[afterKey] = '';
    datesRef.current[beforeKey] = '';
    onPairBlockingChange(afterKey, false);
  }, [resetVersion, afterKey, beforeKey, datesRef, onPairBlockingChange]);

  const syncPair = (nextAfter, nextBefore) => {
    datesRef.current[afterKey] = nextAfter;
    datesRef.current[beforeKey] = nextBefore;
    onPairBlockingChange(afterKey, isPairBlocking(nextAfter, nextBefore));
  };

  const handleAfterChange = (e) => {
    const next = maskDdMmYyyy(e.target.value);
    setAfterValue(next);
    syncPair(next, beforeValue);
  };

  const handleBeforeChange = (e) => {
    const next = maskDdMmYyyy(e.target.value);
    setBeforeValue(next);
    syncPair(afterValue, next);
  };

  const handleAfterBlur = () => {
    const next = constrainAgainstPeer(afterValue, beforeValue, 'after');
    if (next === afterValue) return;
    setAfterValue(next);
    syncPair(next, beforeValue);
  };

  const handleBeforeBlur = () => {
    const next = constrainAgainstPeer(beforeValue, afterValue, 'before');
    if (next === beforeValue) return;
    setBeforeValue(next);
    syncPair(afterValue, next);
  };

  const afterError = getDateFieldError(afterValue);
  const beforeError = getDateFieldError(beforeValue);
  const pairError = getPairError(afterValue, beforeValue);
  const beforeDate = parseDdMmYyyy(beforeValue);
  const afterDate = parseDdMmYyyy(afterValue);

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}
      >
        {label}
      </Typography>
      <TextField
        size="small"
        fullWidth
        label="After (on or after)"
        placeholder="dd/mm/yyyy"
        value={afterValue}
        onChange={handleAfterChange}
        onBlur={handleAfterBlur}
        error={
          Boolean(afterError) || Boolean(pairError && afterValue && beforeValue)
        }
        helperText={
          afterError || (beforeDate ? `Max ${formatDdMmYyyy(beforeDate)}` : ' ')
        }
        sx={{ mb: 1 }}
        inputProps={{
          inputMode: 'numeric',
          maxLength: 10,
          autoComplete: 'off',
        }}
      />
      <TextField
        size="small"
        fullWidth
        label="Before (on or before)"
        placeholder="dd/mm/yyyy"
        value={beforeValue}
        onChange={handleBeforeChange}
        onBlur={handleBeforeBlur}
        error={
          Boolean(beforeError) ||
          Boolean(pairError && afterValue && beforeValue)
        }
        helperText={
          beforeError || (afterDate ? `Min ${formatDdMmYyyy(afterDate)}` : ' ')
        }
        inputProps={{
          inputMode: 'numeric',
          maxLength: 10,
          autoComplete: 'off',
        }}
      />
      {pairError &&
        afterValue &&
        beforeValue &&
        !afterError &&
        !beforeError && <FormHelperText error>{pairError}</FormHelperText>}
    </Box>
  );
});

/**
 * Side drawer filters for Employee Master CSV download.
 */
export default function EmployeeMasterCsvExportDrawer({
  open,
  onClose,
  onDownload,
  isExporting,
}) {
  const theme = useTheme();
  const [status, setStatus] = useState('ALL');
  const [leaveGroup, setLeaveGroup] = useState('ALL');
  const [reportingManagerId, setReportingManagerId] = useState('ALL');
  const [leaveGroups, setLeaveGroups] = useState([]);
  const [reportingManagers, setReportingManagers] = useState([]);
  const [datesBlocking, setDatesBlocking] = useState(false);
  const [resetVersion, setResetVersion] = useState(0);
  const datesRef = useRef({ ...EMPTY_FILTERS });
  const pairBlockingRef = useRef({});
  const leaveGroupsLoadedRef = useRef(false);
  const reportingManagersLoadedRef = useRef(false);

  useEffect(() => {
    if (!open || leaveGroupsLoadedRef.current) return;
    let cancelled = false;
    const load = async () => {
      const response = await getLeaveGroups();
      if (cancelled) return;
      const groups = response?.data?.data || [];
      setLeaveGroups(Array.isArray(groups) ? groups : []);
      leaveGroupsLoadedRef.current = true;
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || reportingManagersLoadedRef.current) return;
    let cancelled = false;
    const load = async () => {
      const response = await getEligibleReportingManagers();
      if (cancelled) return;
      const managers = response?.data?.data || [];
      setReportingManagers(Array.isArray(managers) ? managers : []);
      reportingManagersLoadedRef.current = true;
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handlePairBlockingChange = useCallback((pairId, blocking) => {
    pairBlockingRef.current[pairId] = blocking;
    const anyBlocking = Object.values(pairBlockingRef.current).some(Boolean);
    setDatesBlocking((prev) => (prev === anyBlocking ? prev : anyBlocking));
  }, []);

  const handleReset = () => {
    setStatus('ALL');
    setLeaveGroup('ALL');
    setReportingManagerId('ALL');
    setResetVersion((v) => v + 1);
  };

  const handleDownload = () => {
    if (datesBlocking) return;
    const dateValues = datesRef.current || {};
    onDownload({
      ...EMPTY_FILTERS,
      status,
      leaveGroup,
      reportingManagerId,
      joiningDateAfter: dateValues.joiningDateAfter || '',
      joiningDateBefore: dateValues.joiningDateBefore || '',
      careerStartDateAfter: dateValues.careerStartDateAfter || '',
      careerStartDateBefore: dateValues.careerStartDateBefore || '',
      internshipEndDateAfter: dateValues.internshipEndDateAfter || '',
      internshipEndDateBefore: dateValues.internshipEndDateBefore || '',
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      keepMounted={false}
      ModalProps={{ keepMounted: false }}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
        },
      }}
    >
      <DrawerHeader>
        <IconButton onClick={onClose} aria-label="Close filters">
          {theme.direction === 'rtl' ? (
            <ChevronLeftIcon />
          ) : (
            <ChevronRightIcon />
          )}
        </IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, ml: 1 }}>
          Download filters
        </Typography>
        <IconButton
          style={{ marginLeft: 'auto' }}
          onClick={handleReset}
          title="Reset filters"
          aria-label="Reset filters"
        >
          <RestartAltIcon />
        </IconButton>
      </DrawerHeader>
      <Divider />

      <Box sx={{ p: 2, pb: 10, overflowY: 'auto' }}>
        <FormControl component="fieldset" sx={{ mb: 2.5 }}>
          <FormLabel
            component="legend"
            sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}
          >
            Employee status
          </FormLabel>
          <RadioGroup
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <FormControlLabel
              value="ALL"
              control={<Radio size="small" />}
              label="All"
            />
            <FormControlLabel
              value="ACTIVE"
              control={<Radio size="small" />}
              label="Active only"
            />
            <FormControlLabel
              value="ARCHIVED"
              control={<Radio size="small" />}
              label="Offboarded only"
            />
          </RadioGroup>
        </FormControl>

        <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
          <TextField
            select
            size="small"
            label="Leave group"
            value={leaveGroup}
            onChange={(e) => setLeaveGroup(e.target.value)}
          >
            <MenuItem value="ALL">All leave groups</MenuItem>
            <MenuItem value="NONE">No leave group</MenuItem>
            {leaveGroups.map((group) => (
              <MenuItem key={group.id} value={group.groupName}>
                {group.groupName}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>

        <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
          <TextField
            select
            size="small"
            label="Reporting Manager"
            value={reportingManagerId}
            onChange={(e) => setReportingManagerId(e.target.value)}
          >
            <MenuItem value="ALL">All reporting managers</MenuItem>
            <MenuItem value="NONE">No reporting manager</MenuItem>
            {reportingManagers.map((manager) => (
              <MenuItem key={manager.id} value={manager.id}>
                {`${manager.displayName || ''} - ${manager.employeeCode ?? ''} - ${manager.emailId || ''}`}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>

        <DateBoundFields
          label="Date of joining"
          afterKey="joiningDateAfter"
          beforeKey="joiningDateBefore"
          resetVersion={resetVersion}
          datesRef={datesRef}
          onPairBlockingChange={handlePairBlockingChange}
        />
        <DateBoundFields
          label="Career start date"
          afterKey="careerStartDateAfter"
          beforeKey="careerStartDateBefore"
          resetVersion={resetVersion}
          datesRef={datesRef}
          onPairBlockingChange={handlePairBlockingChange}
        />
        <DateBoundFields
          label="Internship end date"
          afterKey="internshipEndDateAfter"
          beforeKey="internshipEndDateBefore"
          resetVersion={resetVersion}
          datesRef={datesRef}
          onPairBlockingChange={handlePairBlockingChange}
        />

        <Typography
          variant="caption"
          sx={{ color: '#64748b', display: 'block', mb: 2 }}
        >
          Dates use dd/mm/yyyy. Leave a bound blank to ignore that side.
          Providing both filters employees between the two dates (inclusive).
        </Typography>

        <Button
          fullWidth
          variant="contained"
          startIcon={
            isExporting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <DownloadIcon />
            )
          }
          onClick={handleDownload}
          disabled={isExporting || datesBlocking}
        >
          {isExporting ? 'Exporting...' : 'Download CSV'}
        </Button>
      </Box>
    </Drawer>
  );
}
